import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { 
  MapIcon,
  HeartIcon,
  SparklesIcon,
  UsersIcon,
  SunIcon,
  HomeIcon,
  AcademicCapIcon,
  HandRaisedIcon,
  MusicalNoteIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import BookingModal from '../components/BookingModal';

interface ServiceDetail {
  id: string;
  name: string;
  description: string;
  longDescription: string;
  icon: React.ElementType;
  image: string;
  gallery: string[];
  highlights: string[];
  difficulty: 'Easy' | 'Moderate' | 'Challenging';
  category: 'Adventure' | 'Culture' | 'Wellness' | 'Food' | 'Entertainment';
  included: string[];
  notIncluded: string[];
  requirements: string[];
  location: string;
}

const serviceDetails: { [key: string]: ServiceDetail } = {
  'hiking-trekking': {
    id: 'hiking-trekking',
    name: 'Guided Hiking & Trekking',
    description: 'Explore the pristine trails around Bethanchok with experienced local guides.',
    longDescription: 'Embark on an unforgettable journey through the pristine trails surrounding Bethanchok village. Our experienced local guides will lead you through terraced fields, dense forests, and scenic viewpoints offering breathtaking panoramic views of the Himalayas. This adventure combines physical activity with cultural discovery as you encounter local wildlife, traditional farming practices, and hidden gems known only to locals. The trails vary in difficulty, allowing both beginners and experienced hikers to enjoy the natural beauty of Nepal while learning about the local ecosystem and traditional way of life. You will walk through rhododendron forests, cross traditional wooden bridges, and visit remote villages where time seems to have stood still.',
    icon: MapIcon,
    image: '/images/services/hiking.webp',
    gallery: [
      '/images/services/hiking1.jpg',
      '/images/services/hiking2.jpg',
      '/images/services/hiking3.jpeg'
    ],
    highlights: ['Local expert guides', 'Multiple trail options', 'Himalayan viewpoints', 'Photography stops'],
    difficulty: 'Moderate',
    category: 'Adventure',
    included: [
      'Experienced local guide',
      'Trail map and route planning',
      'Basic first aid kit',
      'Photography assistance',
      'Traditional snacks',
      'Bottled water'
    ],
    notIncluded: [
      'Personal hiking gear',
      'Lunch (can be arranged)',
      'Transportation to trailhead',
      'Travel insurance'
    ],
    requirements: [
      'Basic fitness level required',
      'Comfortable hiking shoes',
      'Weather-appropriate clothing',
      'Sun protection (hat, sunscreen)',
      'Camera for memorable shots'
    ],
    location: 'Bethanchok Village Surroundings'
  },
  'yoga-meditation': {
    id: 'yoga-meditation',
    name: 'Morning Yoga & Meditation',
    description: 'Start your day with peaceful yoga sessions in our serene garden.',
    longDescription: 'Begin each day with a transformative yoga and meditation session in our peaceful garden sanctuary. Surrounded by the sounds of nature and the majestic backdrop of the Himalayas, experience a perfect blend of ancient yogic practices and mindful meditation. Our certified instructor will guide you through gentle asanas suitable for all levels, followed by breathing exercises and meditation techniques that will center your mind and energize your body for the day ahead. This practice connects you with the spiritual essence of Nepal while promoting physical wellness and mental clarity. The sessions are designed to help you find inner peace while being surrounded by the natural beauty of the Himalayan foothills.',
    icon: HeartIcon,
    image: '/images/services/yoga.webp',
    gallery: [
      '/images/services/yoga1.avif',
      '/images/services/yoga2.jpg',
      '/images/services/yoga3.jpeg'
    ],
    highlights: ['Certified instructor', 'Mountain backdrop', 'All levels welcome', 'Meditation garden'],
    difficulty: 'Easy',
    category: 'Wellness',
    included: [
      'Certified yoga instructor',
      'Yoga mats and props',
      'Meditation cushions',
      'Herbal tea after session',
      'Printed practice guide'
    ],
    notIncluded: [
      'Personal yoga equipment',
      'Yoga clothing rental'
    ],
    requirements: [
      'Comfortable, flexible clothing',
      'Empty stomach (light breakfast 2 hours before)',
      'Open mind and willingness to learn',
      'Water bottle'
    ],
    location: 'Homestay Garden'
  },
  'village-cultural-tour': {
    id: 'village-cultural-tour',
    name: 'Traditional Village Tours',
    description: 'Immerse yourself in authentic Nepali village life.',
    longDescription: 'Experience the authentic rhythm of Nepali village life through our comprehensive cultural tour. Walk through narrow cobblestone paths, visit traditional homes where families have lived for generations, and observe daily activities like farming, cooking, and handicraft making. You will be welcomed into local homes where you can interact with families, learn about their customs, and understand the deep-rooted traditions that define rural Nepal. Visit ancient temples, witness religious ceremonies, and discover the intricate social fabric that binds the community together. This tour provides genuine insights into how people in rural Nepal live, work, and maintain their cultural heritage in the modern world.',
    icon: UsersIcon,
    image: '/images/services/village_tour.jpg',
    gallery: [
      '/images/services/village_tour1.jpg',
      '/images/services/village_tour2.jpg',
      '/images/services/village_tour3.jpg'
    ],
    highlights: ['Family visits', 'Temple ceremonies', 'Traditional crafts', 'Cultural exchange'],
    difficulty: 'Easy',
    category: 'Culture',
    included: [
      'Cultural guide',
      'Village entry permissions',
      'Traditional welcome drink',
      'Family interaction sessions',
      'Temple visit guidance'
    ],
    notIncluded: [
      'Donations to temples',
      'Shopping expenses',
      'Personal refreshments'
    ],
    requirements: [
      'Respectful attire',
      'Open and curious mindset',
      'Comfortable walking shoes',
      'Small gifts for families (optional)'
    ],
    location: 'Bethanchok Village'
  },
  'cooking-classes': {
    id: 'cooking-classes',
    name: 'Traditional Cooking Classes',
    description: 'Learn the art of Nepali cuisine from our experienced host family.',
    longDescription: 'Discover the secrets of authentic Nepali cuisine in our hands-on cooking classes led by our experienced host family. Learn to prepare traditional dishes like dal bhat (lentils and rice), momos (dumplings), gundruk (fermented leafy greens), and seasonal vegetables using time-honored techniques and locally sourced ingredients. The classes begin with a visit to our organic garden where you will harvest fresh vegetables and herbs. You will learn about the medicinal properties of various spices, the art of balancing flavors, and traditional cooking methods that have been passed down through generations. The experience includes not just cooking but understanding the cultural significance of food in Nepali society.',
    icon: SparklesIcon,
    image: '/images/services/cooking.webp',
    gallery: [
      '/images/services/cooking1.jpg',
      '/images/services/cooking2.jpg',
      '/images/services/cooking3.jpg'
    ],
    highlights: ['Hands-on experience', 'Family recipes', 'Fresh ingredients', 'Take recipes home'],
    difficulty: 'Easy',
    category: 'Food',
    included: [
      'All cooking ingredients',
      'Traditional cooking utensils',
      'Recipe cards to take home',
      'Family cooking guidance',
      'Full meal to enjoy'
    ],
    notIncluded: [
      'Cooking apron',
      'Additional snacks'
    ],
    requirements: [
      'Comfortable clothing',
      'Hair tied back (if long)',
      'Clean hands and trimmed nails',
      'Appetite for delicious food'
    ],
    location: 'Homestay Kitchen'
  },
  'homestay-experience': {
    id: 'homestay-experience',
    name: 'Authentic Homestay Living',
    description: 'Experience genuine Nepali hospitality by staying with our local family.',
    longDescription: 'Live like a local family member in our traditional Nepali homestay experience. This is not just accommodation but a complete cultural immersion where you become part of our family. Participate in daily household activities, help with farming chores, join in cooking traditional meals, and experience the warmth of Nepali hospitality. Wake up to the sounds of roosters crowing and temple bells, share meals sitting on traditional mats, and engage in evening conversations over tea. You will sleep in comfortable traditional rooms, use shared facilities with the family, and truly understand what rural Nepali life is all about. This experience offers genuine insights into family dynamics, community relationships, and the simple joys of village life.',
    icon: HomeIcon,
    image: '/images/services/homestay.jpg',
    gallery: [
      '/images/services/homestay1.jpg',
      '/images/services/homestay2.jpg',
      '/images/services/homestay3.jpg'
    ],
    highlights: ['Family integration', 'Daily activities', 'Cultural immersion', 'Shared meals'],
    difficulty: 'Easy',
    category: 'Culture',
    included: [
      'Traditional accommodation',
      'All meals with family',
      'Participation in daily activities',
      'Cultural orientation',
      'Clean bedding and towels'
    ],
    notIncluded: [
      'Private bathroom',
      'Western-style amenities',
      'Laundry service'
    ],
    requirements: [
      'Open mind and flexibility',
      'Respect for local customs',
      'Basic communication skills',
      'Modest clothing'
    ],
    location: 'Family Home'
  },
  'campfire-folk-music': {
    id: 'campfire-folk-music',
    name: 'Evening Campfire & Folk Music',
    description: 'Gather around the traditional fire pit for an evening of Nepali folk songs.',
    longDescription: 'As the sun sets behind the Himalayan peaks, gather around our traditional fire pit for an enchanting evening of Nepali folk music and storytelling. Local musicians will perform traditional songs using authentic instruments like the madal, sarangi, and flute, while elders share ancient stories passed down through generations. The evening includes traditional dance performances where guests are invited to participate and learn basic steps. Under the star-filled sky, you will experience the oral traditions that have kept Nepali culture alive for centuries. The atmosphere is magical as the fire crackles, music echoes through the valley, and the sense of community brings everyone together in a celebration of culture and friendship.',
    icon: MusicalNoteIcon,
    image: '/images/services/campfire.jpg',
    gallery: [
      '/images/services/campfire1.jpeg',
      '/images/services/campfire2.jpeg',
      '/images/services/campfire3.jpg'
    ],
    highlights: ['Live folk music', 'Traditional stories', 'Cultural dance', 'Stargazing'],
    difficulty: 'Easy',
    category: 'Entertainment',
    included: [
      'Traditional musical performance',
      'Folk dance instruction',
      'Storytelling session',
      'Hot drinks around fire',
      'Traditional snacks'
    ],
    notIncluded: [
      'Additional beverages',
      'Blankets (bring your own)'
    ],
    requirements: [
      'Warm clothing for evening',
      'Comfortable seating cushion',
      'Enthusiasm for music and dance',
      'Camera for capturing memories'
    ],
    location: 'Homestay Courtyard'
  },
  'sunrise-bird-watching': {
    id: 'sunrise-bird-watching',
    name: 'Himalayan Sunrise & Bird Watching',
    description: 'Experience the magical Himalayan sunrise while observing diverse bird species.',
    longDescription: 'Begin your day with the spectacular sight of the sun rising over the Himalayan peaks while discovering the rich bird life of the region. Our early morning expedition takes you to the best vantage points where you can witness the mountains turning golden while listening to the symphony of bird calls. The area around Bethanchok is home to over 200 bird species including colorful sunbirds, majestic eagles, melodious barbets, and rare himalayan pheasants. Our experienced guide will help you identify different species, understand their behaviors, and capture perfect photographs. The experience combines the spiritual upliftment of a Himalayan sunrise with the excitement of wildlife observation, creating memories that will last a lifetime.',
    icon: SunIcon,
    image: '/images/services/sunrise.jpeg',
    gallery: [
      '/images/services/sunrise1.jpg',
      '/images/services/sunrise2.jpg',
      '/images/services/sunrise3.jpg'
    ],
    highlights: ['Himalayan sunrise', 'Bird identification', 'Photography guide', 'Binoculars provided'],
    difficulty: 'Moderate',
    category: 'Adventure',
    included: [
      'Professional binoculars',
      'Bird identification guide',
      'Photography assistance',
      'Light breakfast',
      'Hot tea/coffee'
    ],
    notIncluded: [
      'Professional camera equipment',
      'Transportation to viewing points'
    ],
    requirements: [
      'Early wake-up call (5:00 AM)',
      'Warm clothing for morning',
      'Comfortable walking shoes',
      'Camera with zoom lens (optional)'
    ],
    location: 'Mountain Viewpoints'
  },
  'language-cultural-exchange': {
    id: 'language-cultural-exchange',
    name: 'Nepali Language & Cultural Exchange',
    description: 'Learn basic Nepali phrases and cultural etiquette.',
    longDescription: 'Engage in meaningful cultural exchange while learning basic Nepali language skills that will enhance your travel experience and deepen your connection with local people. Our interactive sessions cover essential phrases, cultural etiquette, and social customs that will help you navigate daily interactions with confidence and respect. You will learn the art of greeting, expressing gratitude, basic numbers, and common conversational phrases. The program also includes cultural orientation covering topics like religious practices, social hierarchies, family structures, and festivals. This two-way exchange allows you to share your own culture while gaining insights into Nepali society, creating bridges of understanding and friendship.',
    icon: AcademicCapIcon,
    image: '/images/services/culture.jpg',
    gallery: [
      '/images/services/culture1.jpeg',
      '/images/services/culture2.jpeg',
      '/images/services/culture3.jpg'
    ],
    highlights: ['Basic Nepali lessons', 'Cultural etiquette', 'Language practice', 'Cultural sharing'],
    difficulty: 'Easy',
    category: 'Culture',
    included: [
      'Structured language lessons',
      'Cultural etiquette guide',
      'Practice conversation sessions',
      'Written materials to take home',
      'Cultural exchange activities'
    ],
    notIncluded: [
      'Advanced language materials',
      'Certified language certificate'
    ],
    requirements: [
      'Notebook and pen',
      'Open mind for learning',
      'Willingness to share your culture',
      'Basic English communication skills'
    ],
    location: 'Homestay Common Area'
  },
  'organic-farming': {
    id: 'organic-farming',
    name: 'Organic Farming Experience',
    description: 'Get hands-on experience with traditional organic farming methods.',
    longDescription: 'Immerse yourself in sustainable agriculture practices by participating in our organic farming activities. Learn traditional farming techniques that have been practiced in Nepal for generations, including natural composting, crop rotation, and chemical-free pest management. You will work alongside local farmers in terraced fields, plant and harvest seasonal crops, and understand the challenges and rewards of mountain agriculture. The experience includes learning about indigenous seeds, traditional tools, water management systems, and the relationship between farming and local ecosystems. This hands-on activity provides insights into food security, environmental sustainability, and the hard work behind every meal while connecting you with the land and its caretakers.',
    icon: HandRaisedIcon,
    image: '/images/services/farming.jpg',
    gallery: [
      '/images/services/farming1.jpg',
      '/images/services/farming2.jpg',
      '/images/services/farming3.jpg'
    ],
    highlights: ['Organic methods', 'Seasonal activities', 'Farm-to-table', 'Sustainability focus'],
    difficulty: 'Moderate',
    category: 'Culture',
    included: [
      'Farm tool usage training',
      'Organic farming guidance',
      'Seasonal activity participation',
      'Farm-fresh meal',
      'Educational materials'
    ],
    notIncluded: [
      'Work gloves',
      'Rain gear',
      'Personal protective equipment'
    ],
    requirements: [
      'Work-appropriate clothing',
      'Comfortable shoes that can get dirty',
      'Sun protection',
      'Willingness to do physical work'
    ],
    location: 'Homestay Farm Fields'
  },
  'mountain-meditation': {
    id: 'mountain-meditation',
    name: 'Mountain Peak Meditation',
    description: 'Experience profound peace with guided meditation sessions at scenic mountain viewpoints.',
    longDescription: 'Ascend to serene mountain viewpoints for transformative meditation sessions that combine spiritual practice with the awe-inspiring beauty of the Himalayas. These guided meditation experiences take place at carefully selected locations offering panoramic mountain views and natural silence broken only by the sounds of wind and distant bird calls. You will learn various meditation techniques including mindfulness, breathing exercises, and walking meditation while being surrounded by some of the most peaceful environments on earth. The sessions are designed to help you disconnect from daily stress and connect with your inner self while being inspired by the majesty of nature. Each session includes preparation, guided practice, and reflection time.',
    icon: GlobeAltIcon,
    image: '/images/services/mountain_meditation.jpg',
    gallery: [
      '/images/services/mountain_meditation1.jpg',
      '/images/services/mountain_meditation2.jpg',
      '/images/services/mountain_meditation3.jpg'
    ],
    highlights: ['Guided meditation', 'Mountain viewpoints', 'Nature connection', 'Mindfulness practice'],
    difficulty: 'Moderate',
    category: 'Wellness',
    included: [
      'Meditation guidance',
      'Meditation cushions',
      'Peaceful location access',
      'Breathing technique instruction',
      'Reflection journal'
    ],
    notIncluded: [
      'Personal meditation equipment',
      'Transportation to remote locations'
    ],
    requirements: [
      'Comfortable meditation clothing',
      'Water bottle',
      'Open mind for spiritual practice',
      'Basic hiking ability for location access'
    ],
    location: 'Mountain Viewpoints'
  }
};

export default function ServiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const service = id ? serviceDetails[id] : null;
  const [isBookingOpen, setIsBookingOpen] = React.useState(false);

  if (!service) {
    return (
      <div className="bg-white py-24 sm:py-32 page-transition">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-neutral-900">Service Not Found</h1>
          <p className="mt-4 text-neutral-600">The service you're looking for doesn't exist.</p>
          <Link 
            to="/services"
            className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white page-transition">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <img
          src={service.image}
          alt={service.name}
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-neutral-900/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="flex items-center justify-center mb-4">
              <service.icon className="h-12 w-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{service.name}</h1>
            <p className="text-xl md:text-2xl max-w-2xl mx-auto">{service.description}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 lg:px-8 py-16">
        {/* Back Button */}
        <Link
          to="/services"
          className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 transition-colors duration-200"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Services
        </Link>
        {/* Service Info Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-primary-50 rounded-lg p-4 text-center">
            <div className="text-sm font-medium text-neutral-900">{service.category}</div>
            <div className="text-xs text-neutral-600">Category</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className={`text-sm font-medium ${
              service.difficulty === 'Easy' ? 'text-green-600' :
              service.difficulty === 'Moderate' ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {service.difficulty}
            </div>
            <div className="text-xs text-neutral-600">Difficulty</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <MapPinIcon className="h-5 w-5 text-green-600 mx-auto mb-1" />
            <div className="text-xs text-neutral-600">{service.location}</div>
          </div>
        </div>

        {/* Gallery - moved up */}
        {service.gallery.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-bold text-neutral-900 mb-4">Experience Gallery</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {service.gallery.map((image, index) => (
                <div key={index} className="aspect-[4/3] overflow-hidden rounded-lg">
                  <img
                    src={image}
                    alt={`${service.name} ${index + 1}`}
                    className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Description */}
        <div className="prose prose-lg max-w-none mb-8">
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">About This Experience</h2>
          <p className="text-neutral-700 leading-relaxed">{service.longDescription}</p>
        </div>

        {/* Highlights */}
        <div className="mb-8">
          <h3 className="text-xl font-bold text-neutral-900 mb-4">Experience Highlights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {service.highlights.map((highlight, index) => (
              <div key={index} className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-neutral-700">{highlight}</span>
              </div>
            ))}
          </div>
        </div>

        {/* What's Included / Not Included */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xl font-bold text-neutral-900 mb-4">What's Included</h3>
            <ul className="space-y-2">
              {service.included.map((item, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold text-neutral-900 mb-4">Not Included</h3>
            <ul className="space-y-2">
              {service.notIncluded.map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className="h-5 w-5 border-2 border-neutral-300 rounded mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Requirements */}
        <div className="bg-neutral-50 rounded-lg p-6 mb-8">
          <h4 className="font-bold text-neutral-900 mb-4">What to Bring & Requirements</h4>
          <ul className="space-y-2">
            {service.requirements.map((req, index) => (
              <li key={index} className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-2 mt-2 flex-shrink-0" />
                <span className="text-sm text-neutral-700">{req}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-8 text-center">
          <h3 className="text-xl font-bold text-neutral-900 mb-4">Ready to Experience This?</h3>
          <p className="text-neutral-600 mb-6">Book your stay to enjoy this authentic Nepali experience during your homestay.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              className="px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200"
              onClick={() => setIsBookingOpen(true)}
            >
              Book Your Stay
            </button>
            <Link
              to="/services"
              className="px-6 py-3 border border-primary-600 text-primary-600 rounded-lg font-semibold hover:bg-primary-50 transition-colors duration-200"
            >
              View More Services
            </Link>
          </div>
          <BookingModal isOpen={isBookingOpen} closeModal={() => setIsBookingOpen(false)} />
        </div>
      </div>
    </div>
  );
}
